import React, { useEffect, useRef, useState } from "react";
import PageTitleBanner from "../../Components/Reusable/PageTitleBanner";
import { SvgImages } from "../../Utils/LocalImages";
import Image from "../../Components/Reusable/Image";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputType, handleApiErrors, validationRegex } from "../../Utils/Utils";
import { ImageFilters, StudentRegistrationType } from "../../Utils/Enums";
import SubmitButton from "../../Components/Reusable/Buttons";
import CustomTextField from "../../Components/Reusable/CustomTextField";
import CustomDropDown from "../../Components/Reusable/CustomDropDown";
import DataProvider from "../../Utils/DataProvider";
import ApiService from "../../Services/ApiService";
import StudentApiService from "../../Services/StudentApiService";
import { useNavigate } from "react-router-dom";
import RoutesPath from "../../Utils/RoutesPath";
import CustomDatePicker from "../../Components/Reusable/CustomDatePicker";

const StudentRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [resetDistrict, setResetDistrict] = useState(false);
  const [resetTaluka, setResetTaluka] = useState(false);
  const [resetPinCode, setResetPinCode] = useState(false);
  const [classes, setClasses] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [userPhoneNumber, setUserPhoneNumber] = useState("");
  const [userEmailAddress, setUserEmailAddress] = useState("");
  const [personalDetails, setPersonalDetails] = useState();
  const [contactDetails, setContactDetails] = useState();
  const [tempUserToken, setTempUserToken] = useState();
  const [selectedState, setSelectedState] = useState("Maharashtra");
  const [registerBy, setRegisterBy] = useState();

  const [showDistrict, setShowDistrict] = useState(true);
  const [showTaluka, setShowTaluka] = useState(true);
  const [showPinCode, setShowPinCode] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    getAllClasses();
    getAllStates();
  }, []);

  const getAllClasses = async () => {
    try {
      const response = await ApiService.getAllClasses();
      setClasses(response.data);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const getAllPincodes = async (taluka_id) => {
    try {
      const response = await ApiService.getAllPincodes(taluka_id);
      setShowPinCode(response.data.length > 0);
      setPincodes(response.data);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const getAllTalukas = async (district_id) => {
    try {
      const response = await ApiService.getAllTalukas(district_id);
      setShowPinCode(response.data.length > 0);
      setShowTaluka(response.data.length > 0);
      setTalukas(response.data);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const getAllDistricts = async (state_id) => {
    try {
      const response = await ApiService.getAllDistricts(state_id);
      setShowDistrict(response.data.length > 0);
      setShowPinCode(response.data.length > 0);
      setShowTaluka(response.data.length > 0);
      setDistricts(response.data);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const getAllStates = async () => {
    try {
      const response = await ApiService.getAllStates();
      setStates(response.data);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const validationSchemaPersonalDetails = Yup.object().shape({
    firstName: validationRegex.required,
    middleName: validationRegex.required,
    lastName: validationRegex.required,
    dateOfBirth: validationRegex.dob,
    gender: validationRegex.required,
    schoolName: validationRegex.required,
    medium: validationRegex.required,
    class_name: validationRegex.required,
    RegisterBy: validationRegex.required,
    referralCode: Yup.string().when("RegisterBy", (registerBy, schema) => {
      if (registerBy[0] === StudentRegistrationType.coordinator)
        return schema.required("Field can't be empty!");
      return schema;
    }),
  });

  const {
    register: registerPersonalDetails,
    handleSubmit: handleSubmitPersonalDetails,
    setValue: setValuePersonalDetails,
    formState: {
      errors: errorsPersonalDetails,
      isValid: isPersonalDetailsValid,
    },
  } = useForm({
    resolver: yupResolver(validationSchemaPersonalDetails),
  });

  const validationSchemaContactDetails = Yup.object().shape({
    email: validationRegex.email,
    mobile: validationRegex.mobile,
    addressLineOne: validationRegex.required,
    addressLineTwo: validationRegex.required,
    state: validationRegex.required,
    district: showDistrict
      ? validationRegex.required
      : Yup.string().notRequired(),
    taluka: showTaluka ? validationRegex.required : Yup.string().notRequired(),
    pinCode: showPinCode
      ? validationRegex.required
      : Yup.string().notRequired(),
    terms: validationRegex.terms,
  });

  const {
    register: registerContactDetails,
    handleSubmit: handleSubmitContactDetails,
    setValue: setValueContactDetails,
    getValues: getValueContactDetails,
    watch: watchContactDetails,
    formState: { errors: errorsContactDetails },
  } = useForm({
    resolver: yupResolver(validationSchemaContactDetails),
  });

  useEffect(() => {
    setResetTaluka(true);
    setResetPinCode(true);
    if (
      watchContactDetails("district") &&
      watchContactDetails("district") != ""
    )
      getAllTalukas(watchContactDetails("district"));
  }, [watchContactDetails("district")]);

  useEffect(() => {
    setResetDistrict(true);
    setResetTaluka(true);
    setResetPinCode(true);
    if (watchContactDetails("state") && watchContactDetails("state") != "")
      getAllDistricts(watchContactDetails("state"));
  }, [watchContactDetails("state")]);

  useEffect(() => {
    setResetPinCode(true);
    if (watchContactDetails("taluka") && watchContactDetails("taluka") != "")
      getAllPincodes(watchContactDetails("taluka"));
  }, [watchContactDetails("taluka")]);

  // - From Submittion
  const SubmitPersonalDetails = async (data) => {
    if (data.RegisterBy === StudentRegistrationType.coordinator) {
      try {
        await StudentApiService.verifyCoordinatorUniqueCode(data.referralCode);
        proceedToContactDetails(data);
      } catch (error) {
        handleApiErrors(error);
      }
    } else {
      proceedToContactDetails(data);
    }
  };

  const proceedToContactDetails = (data) => {
    setPersonalDetails(data);
    setCurrentStep(2);
    getAllStates();
    setTimeout(() => {
      document.getElementById("ScrollContainer").scrollTo({
        top: 0,
        behavior: "smooth", // Optional: for smooth scrolling
      });
    }, 0);
  };

  const SubmitContactDetails = async (data) => {  
    try {
      setContactDetails(data);
      const response = await StudentApiService.registerStudent({
        ...personalDetails,
        ...data,
      });    

      setTempUserToken(response.token);
      setUserPhoneNumber(getValueContactDetails("mobile"));
      setUserEmailAddress(getValueContactDetails("email"));
      startOtpCountDown();
      setCurrentStep(3);
      setTimeout(() => {
        document.getElementById("ScrollContainer").scrollTo({
          top: 0,
          behavior: "smooth", // Optional: for smooth scrolling
        });
      }, 0);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const inputRefs = useRef([]);
  const otpCount = 6;

  // Function to handle input change
  const handleChange = (event, index) => {
    const value = event.target.value;

    // if (value && index === inputRefs.current.length - 1) {
    //   handleOtpSubmit();
    //   return;
    // }

    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const maskPhoneNumber = (str) => {
    if (str.length <= 2) {
      return str; // If the string length is 2 or less, return the string as is.
    }
    const maskedPart = "X".repeat(str.length - 2);
    const visiblePart = str.slice(-2);
    return maskedPart + visiblePart;
  };

  const handleKeyPress = (event) => {
    // Only ASCII character in that range allowed
    let ASCIICode = event.which ? event.which : event.keyCode;
    if (
      !(
        (ASCIICode >= 47 && ASCIICode <= 57) ||
        (ASCIICode >= 96 && ASCIICode <= 106) ||
        ASCIICode === 8
      )
    ) {
      event.preventDefault();
      return;
    }

    const index = event.target.id;
    if (event.key === "Backspace" && index > 0) {
      if (inputRefs.current[index].value === "") {
        inputRefs.current[index - 1].focus();
        inputRefs.current[index - 1].value = "";
      }
    } else if (inputRefs.current[index].value !== "") {
      event.preventDefault();
      inputRefs.current[index].value = event.key;
      return;
    }
  };

  const getOtpValue = () => {
    return inputRefs.current
      .map((e) => e.value)
      .join("")
      .toString();
  };

  const handleOtpSubmit = async () => {
    const otp = getOtpValue().trim();
    if (!otp.trim() || otp.length !== otpCount) {
      toast.warning("OTP must be " + otpCount.toString() + " digits");
      return;
    }
    try {
      const response = await StudentApiService.verifyOtpRegistrationStudent(
        tempUserToken,
        otp
      );
      toast.success(response.message);
      navigate(RoutesPath.home);
    } catch (error) {
      handleApiErrors(error);
    }
  };

  const timerInterval = 120;
  const [timeLeft, setTimeLeft] = useState(timerInterval); // 120 seconds
  const [timer, setTimer] = useState();
  const [resendOTPEnabled, setResendOTPEnabled] = useState(false);

  const startOtpCountDown = () => {
    setResendOTPEnabled(false);
    setTimeLeft(timerInterval);
    clearInterval(timer);
    setTimer(
      setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            setResendOTPEnabled(true);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000)
    );
    return () => clearInterval(timer);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  return (
    <div className="d-flex flex-column mb-auto">
      <PageTitleBanner PageTitle={"Student Registration"} breadCrumpPageTitle={"Student Registration"}/>
      <div className="text-content-page-container d-flex flex-column pt-0">
        <div className="d-flex flex-column align-items-center">
          <div className="progress_bar px-3">
            <div
              className={`step-${currentStep}`}
              id="checkout-progress"
              data-current-step="1"
            >
              <div className="progress-bar">
                <div
                  onClick={() => {
                    if (currentStep === 2) setCurrentStep(1);
                  }}
                  className={`step step-1 cursor-pointer ${
                    currentStep === 1
                      ? "active"
                      : currentStep > 1
                      ? "valid"
                      : ""
                  }`}
                >
                  <Image
                    className="w-75"
                    src={SvgImages.personalDetails}
                    style={{
                      filter:
                        currentStep >= 1
                          ? ImageFilters.white.filter
                          : ImageFilters.black.filter,
                    }}
                  />
                </div>
                <div
                  className={`step step-2 ${
                    currentStep === 2
                      ? "active"
                      : currentStep > 2
                      ? "valid"
                      : ""
                  }`}
                >
                  <Image
                    className="w-75"
                    src={SvgImages.phone}
                    style={{
                      filter:
                        currentStep >= 2
                          ? ImageFilters.white.filter
                          : ImageFilters.black.filter,
                    }}
                  />
                </div>
                <div
                  className={`step step-3 ${
                    currentStep === 3
                      ? "active"
                      : currentStep > 3
                      ? "valid"
                      : ""
                  }`}
                >
                  <Image
                    className="w-75"
                    src={SvgImages.otpVerification}
                    style={{
                      filter:
                        currentStep >= 3
                          ? ImageFilters.white.filter
                          : ImageFilters.black.filter,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="stepper-labels-container d-flex justify-content-between w-100 gap-3 text-center px-3">
            <h2 className="progress-label mb-0 fw-600 medium-text">
              Personal Details
            </h2>
            <h2 className="progress-label mb-0 fw-600 medium-text">
              Contact Details
            </h2>
            <h2 className="progress-label mb-0 fw-600 medium-text">
              OTP Verification
            </h2>
          </div>

          <div className="stepper-bottom-line" />
        </div>

        <form
          action=""
          className={`${
            currentStep === 1 ? "d-flex" : "d-none"
          } flex-column gap-4`}
          onSubmit={handleSubmitPersonalDetails(SubmitPersonalDetails)}
        >
          <h2 className="mb-0 fw-600 large-text darkBlueColor">
            Personal Details
          </h2>

          <div className="row g-4">
            <div className="col-md-4">
              <CustomTextField
                labelTitle="First Name"
                id="firstName"
                name="firstName"
                inputType={InputType.text}
                placeholder="Enter your first name"
                showError={errorsPersonalDetails?.firstName}
                errorMessage={errorsPersonalDetails?.firstName?.message}
                register={registerPersonalDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="Middle Name"
                id="middleName"
                name="middleName"
                inputType={InputType.text}
                placeholder="Enter your middle name"
                showError={errorsPersonalDetails?.middleName}
                errorMessage={errorsPersonalDetails?.middleName?.message}
                register={registerPersonalDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="Last Name"
                id="lastName"
                name="lastName"
                inputType={InputType.text}
                placeholder="Enter your last name"
                showError={errorsPersonalDetails?.lastName}
                errorMessage={errorsPersonalDetails?.lastName?.message}
                register={registerPersonalDetails}
              />
            </div>

            <div className="col-md-4 custom-datepicker">
              <CustomDatePicker
                labelTitle="Date of Birth"
                id="dateOfBirth"
                name="dateOfBirth"
                inputType={InputType.date}
                placeholder="Enter your DOB"
                isWhiteTheme={true}
                setValue={setValuePersonalDetails}
                showError={errorsPersonalDetails?.dateOfBirth}
                errorMessage={errorsPersonalDetails?.dateOfBirth?.message}
                register={registerPersonalDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomDropDown
                labelTitle="Gender"
                id="gender"
                name="gender"
                placeholder="Select Gender"
                showError={errorsPersonalDetails?.gender}
                errorMessage={errorsPersonalDetails?.gender?.message}
                register={registerPersonalDetails}
                setValue={setValuePersonalDetails}
                options={DataProvider.Genders}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="School Name"
                id="schoolName"
                name="schoolName"
                inputType={InputType.text}
                placeholder="Enter your school name"
                showError={errorsPersonalDetails?.schoolName}
                errorMessage={errorsPersonalDetails?.schoolName?.message}
                register={registerPersonalDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomDropDown
                labelTitle="Medium"
                id="medium"
                name="medium"
                placeholder="Select Medium"
                showError={errorsPersonalDetails?.medium}
                errorMessage={errorsPersonalDetails?.medium?.message}
                register={registerPersonalDetails}
                options={DataProvider.mediumList}
                setValue={setValuePersonalDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomDropDown
                labelTitle="Class"
                id="class_name"
                name="class_name"
                placeholder="Select Class"
                showError={errorsPersonalDetails?.class_name}
                errorMessage={errorsPersonalDetails?.class_name?.message}
                register={registerPersonalDetails}
                setValue={setValuePersonalDetails}
                options={classes?.map((classData) => ({                  
                  value: classData._id,
                  label: classData.class_name,
                }))}
              />
            </div>

            <div className="col-md-4">
              <CustomDropDown
                labelTitle="Register By"
                id="RegisterBy"
                name="RegisterBy"
                placeholder="Register By"
                showError={errorsPersonalDetails?.RegisterBy}
                errorMessage={errorsPersonalDetails?.RegisterBy?.message}
                register={registerPersonalDetails}
                setValue={setValuePersonalDetails}
                onChange={(e) => {
                  setRegisterBy(e.value);
                }}
                options={DataProvider.RegisterBy}
              />
            </div>

            {registerBy == StudentRegistrationType.coordinator ? (
              <div className="col-md-4">
                <CustomTextField
                  labelTitle="Unique Code"
                  id="referralCode"
                  name="referralCode"
                  showError={errorsPersonalDetails?.referralCode}
                  errorMessage={errorsPersonalDetails?.referralCode?.message}
                  placeholder="Enter unique code"
                  register={registerPersonalDetails}
                  setValue={setValuePersonalDetails}
                />
              </div>
            ) : null}
          </div>

          <div className={`d-flex justify-content-end mt-4`}>
            <SubmitButton
              title={"Next"}
              onClick={() => {
                handleSubmitPersonalDetails(SubmitPersonalDetails);
              }}
            />
          </div>
        </form>

        <form
          action=""
          className={`${
            currentStep === 2 ? "d-flex" : "d-none"
          } flex-column gap-4`}
          onSubmit={handleSubmitContactDetails(SubmitContactDetails)}
        >
          <h2 className="mb-0 fw-600 large-text darkBlueColor">
            Contact Details
          </h2>

          <div className="row g-4">
            <div className="col-md-4">
              <CustomTextField
                labelTitle="Email"
                id="email"
                name="email"
                inputType={InputType.text}
                placeholder="Enter your email id"
                showError={errorsContactDetails?.email}
                errorMessage={errorsContactDetails?.email?.message}
                register={registerContactDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="Mobile"
                id="mobile"
                name="mobile"
                inputType={InputType.number}
                placeholder="Enter your mobile no."
                showError={errorsContactDetails?.mobile}
                errorMessage={errorsContactDetails?.mobile?.message}
                register={registerContactDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="Address Line-1"
                id="addressLineOne"
                name="addressLineOne"
                inputType={InputType.text}
                placeholder="Enter your address"
                showError={errorsContactDetails?.addressLineOne}
                errorMessage={errorsContactDetails?.addressLineOne?.message}
                register={registerContactDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomTextField
                labelTitle="Address Line-2"
                id="addressLineTwo"
                name="addressLineTwo"
                inputType={InputType.text}
                placeholder="Enter your address"
                showError={errorsContactDetails?.addressLineTwo}
                errorMessage={errorsContactDetails?.addressLineTwo?.message}
                register={registerContactDetails}
              />
            </div>

            <div className="col-md-4">
              <CustomDropDown
                labelTitle="State"
                id="state"
                name="state"
                placeholder="Select State"
                showError={errorsContactDetails?.state}
                errorMessage={errorsContactDetails?.state?.message}
                register={registerContactDetails}
                setValue={setValueContactDetails}
                options={states.map((e) => {
                  return { value: e._id, label: e.name };
                })}
              />
            </div>

            {showDistrict ? (
              <div className="col-md-4">
                <CustomDropDown
                  labelTitle="District"
                  id="district"
                  name="district"
                  placeholder="Select District"
                  showError={errorsContactDetails?.district}
                  errorMessage={errorsContactDetails?.district?.message}
                  register={registerContactDetails}
                  setValue={setValueContactDetails}
                  resetValue={resetDistrict}
                  setResetValue={setResetDistrict}
                  options={districts.map((e) => {
                    return { value: e._id, label: e.name };
                  })}
                />
              </div>
            ) : null}

            {showTaluka ? (
              <div className="col-md-4">
                <CustomDropDown
                  labelTitle="Taluka"
                  id="taluka"
                  name="taluka"
                  placeholder="Select Taluka"
                  showError={errorsContactDetails?.taluka}
                  errorMessage={errorsContactDetails?.taluka?.message}
                  register={registerContactDetails}
                  setValue={setValueContactDetails}
                  resetValue={resetTaluka}
                  setResetValue={setResetTaluka}
                  options={talukas.map((e) => {
                    return { value: e._id, label: e.name };
                  })}
                />
              </div>
            ) : null}

            {showPinCode ? (
              <div className="col-md-4">
                <CustomDropDown
                  labelTitle="Pin Code"
                  id="pinCode"
                  name="pinCode"
                  placeholder="Select Pin Code"
                  showError={errorsContactDetails?.pinCode}
                  errorMessage={errorsContactDetails?.pinCode?.message}
                  register={registerContactDetails}
                  setValue={setValueContactDetails}
                  resetValue={resetPinCode}
                  setResetValue={setResetPinCode}
                  options={pincodes.map((e) => {
                    return { value: e._id, label: e.name };
                  })}
                />
              </div>
            ) : null}
          </div>

          <div className={`d-flex justify-content-between mt-4`}>
            <div>
              <div className="d-flex align-item-center">
                <input
                  type="checkbox"
                  id="terms"
                  style={{ height: "20px", width: "20px" }}
                  {...registerContactDetails("terms")}
                />
                <label
                  htmlFor="terms"
                 className="ms-2 small-medium-text black fw-500"
                >
                By submitting this form, I agree that all information provided is true and accurate. Any false information may result in disqualification.
                </label>
              </div>
              <div className="d-flex justify-content-start">
                {errorsContactDetails?.terms && (
                  <span className="danger small-text fw-400 mt-2">
                    {errorsContactDetails?.terms?.message}
                  </span>
                )}
              </div>
            </div>
            <SubmitButton
              title={"Next"}
              onClick={() => {
                handleSubmitContactDetails(SubmitContactDetails);
              }}
            />
          </div>
        </form>

        <div
          className={`${
            currentStep === 3 ? "d-flex" : "d-none"
          } flex-column gap-4 align-items-start w-100`}
        >
          <div className="d-flex flex-column gap-2">
            <h2 className="mb-0 fw-600 large-text darkBlueColor">
              OTP Verification
            </h2>
            <h2 className="mb-0 fw-400 medium-text">{`A One-Time Password has been sent to ${maskPhoneNumber(
              userPhoneNumber
            )} and ${userEmailAddress}`}</h2>
          </div>

          <div className="d-flex flex-column gap-2">
            <h2 className="mb-0 fw-600 medium-text black">OTP</h2>

            <div className="d-flex gap-3 justify-content-between">
              {[...Array(otpCount)].map((_, index) => (
                <input
                  key={index}
                  id={index}
                  ref={(ref) => (inputRefs.current[index] = ref)}
                  type={InputType.number}
                  className="otp_number_input Sub-Heading fw-600"
                  onChange={(event) => handleChange(event, index)}
                  onKeyDown={handleKeyPress}
                  maxLength={1}
                />
              ))}
            </div>

            <div className="d-flex justify-content-between gap-3 mt-2 w-100 align-items-center">
              <h2
                className="mb-0 fw-600 small-text black"
                style={{ opacity: resendOTPEnabled ? 0 : 1 }}
              >
                {formatTime(timeLeft)}
              </h2>
              <h2
                className="mb-0 fw-600 small-text orangeAccentColor cursor-pointer"
                style={{ opacity: resendOTPEnabled ? 1 : 0.5 }}
                onClick={() => {
                  if (resendOTPEnabled) {
                    SubmitContactDetails(contactDetails);
                  }
                }}
              >
                Resend OTP
              </h2>
            </div>
          </div>

          <div className={`d-flex align-self-end`}>
            <SubmitButton
              title={"Submit"}
              onClick={() => {
                handleOtpSubmit();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;
